/**
 * Timezone Context for app-wide timezone access
 * Provides user timezone with automatic detection and caching
 */

import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { getUserTimezone, setUserTimezone } from '@/utils/timezone';

interface TimezoneContextType {
  timezone: string;
  updateTimezone: (newTimezone: string) => void;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

interface TimezoneProviderProps {
  children: ReactNode;
}

export function TimezoneProvider({ children }: TimezoneProviderProps) {
  const [timezone, setTimezoneState] = useState<string>(() => {
    // Initialize timezone during state initialization
    const initialTimezone = getUserTimezone();
    return initialTimezone;
  });

  const updateTimezone = useCallback((newTimezone: string) => {
    setUserTimezone(newTimezone);
    setTimezoneState(newTimezone);
  }, []);

  return (
    <TimezoneContext.Provider value={{
      timezone,
      updateTimezone
    }}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone(): TimezoneContextType {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
}